import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import { useEffect } from "react";

import { ApiError } from "@/api/client";
import { useDeleteEvDcModbusTarget } from "@/api/queries";
import type { EvDcModbusTarget } from "@/api/types";

export type EvDcModbusTargetDeleteConfirmProps = {
  opened: boolean;
  onClose: () => void;
  target: EvDcModbusTarget | null;
};

export function EvDcModbusTargetDeleteConfirm({
  opened,
  onClose,
  target,
}: EvDcModbusTargetDeleteConfirmProps) {
  const deleteMutation = useDeleteEvDcModbusTarget();

  useEffect(() => {
    if (opened) deleteMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, target?.id]);

  const handleDelete = async () => {
    if (!target) return;
    try {
      await deleteMutation.mutateAsync(target.id);
      onClose();
    } catch {
      // Error is rendered inline below.
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Delete EV DC Modbus target" centered>
      <Stack>
        {deleteMutation.error && <DeleteErrorAlert error={deleteMutation.error} />}
        <Text>
          Delete{" "}
          <Text span fw={600}>
            {target?.name ?? "this target"}
          </Text>
          ?
        </Text>
        <Text size="sm" c="dimmed">
          Historical EV charger command audit rows remain unchanged.
        </Text>
        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={onClose} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button color="red" onClick={() => void handleDelete()} loading={deleteMutation.isPending}>
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function DeleteErrorAlert({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? `${error.code}: ${error.message}`
      : error instanceof Error
        ? error.message
        : "Unknown error";
  return (
    <Alert icon={<IconAlertCircle size={18} />} color="red" variant="light" title="Delete failed">
      {message}
    </Alert>
  );
}
